import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Col,
  Descriptions,
  Drawer,
  Form,
  Image,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Tag,
  TreeSelect,
  Typography,
  type TableProps,
} from 'antd';
import {
  CalculatorOutlined,
  DeleteOutlined,
  DollarOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  LockOutlined,
  ReloadOutlined,
  UnlockOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApiError,
  FilterDrawer,
  ResponsiveList,
  snapshotToProductImages,
  useBreakpoint,
  type PageResponse,
  type ProductImagesValue,
} from '@mall/shared';
import { ProductImageEditor } from '@mall/shared/ui';
import { useToast } from '../../app/ToastProvider';
import { listCategories } from '../categories/api';
import { categoryDisplayName, type CategoryView } from '../categories/types';
import { listTenants } from '../tenants/api';
import type { TenantView } from '../tenants/types';
import {
  batchDeleteProducts,
  batchGenerateProductPrice,
  batchPublishProducts,
  batchUnpublishProducts,
  batchUpdateProductPrice,
  getProduct,
  listBrands,
  listProducts,
  setProductSyncLock,
  unpublishProduct,
  updateProductImages,
  updateProductPrice,
} from './api';
import { matchesProductFilters } from './matchesFilters';
import type { BrandView, ProductSnapshot } from './types';

const PRICE = (cents: number) => `¥ ${(cents / 100).toFixed(2)}`;
const PUBLISHED_STATUS = 1;
const STATUS_LABEL: Record<number, string> = {
  0: '草稿',
  1: '已发布',
  2: '已下架',
};
const STATUS_COLOR: Record<number, string> = {
  0: 'default',
  1: 'green',
  2: 'orange',
};

interface FilterFormValues {
  keyword?: string;
  categoryId?: number;
  brandId?: number;
  status?: number;
  priceMinYuan?: number | null;
  priceMaxYuan?: number | null;
  originalPriceMinYuan?: number | null;
  originalPriceMaxYuan?: number | null;
}

interface PriceFormValues {
  priceYuan: number;
}

interface CoefficientFormValues {
  coefficient: number;
}

interface CategoryTreeOption {
  title: string;
  value: number;
  children: CategoryTreeOption[];
}

function buildCategoryOptions(rows: CategoryView[]): CategoryTreeOption[] {
  const nodes = new Map<number, CategoryTreeOption>();
  for (const row of rows) {
    nodes.set(row.id, { title: categoryDisplayName(row), value: row.id, children: [] });
  }
  const roots: CategoryTreeOption[] = [];
  for (const row of rows) {
    const node = nodes.get(row.id);
    if (!node) continue;
    if (row.parentId && nodes.has(row.parentId)) {
      nodes.get(row.parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }
  return roots;
}

function yuanToCents(value: number | null | undefined): number | undefined {
  return value == null ? undefined : Math.round(value * 100);
}

function parseImages(record: Pick<ProductSnapshot, 'mainImage' | 'images'> | null): string[] {
  if (!record) return [];
  const urls: string[] = [];
  if (record.mainImage) {
    urls.push(record.mainImage);
  }
  if (record.images) {
    try {
      const parsed = JSON.parse(record.images) as unknown;
      if (Array.isArray(parsed)) {
        for (const item of parsed) {
          if (typeof item === 'string' && item) {
            urls.push(item);
          }
        }
      }
    } catch {
      // The backend normally returns a JSON array string. Ignore malformed legacy values.
    }
  }
  return Array.from(new Set(urls));
}

function isPublished(record: ProductSnapshot): boolean {
  return record.status === PUBLISHED_STATUS;
}

export function ProductsPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const breakpoint = useBreakpoint();
  const isMobile = breakpoint === 'mobile';
  const [tenantId, setTenantId] = useState<number | undefined>();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState<FilterFormValues>({});
  const [selectedProductIds, setSelectedProductIds] = useState<number[]>([]);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [priceRecord, setPriceRecord] = useState<ProductSnapshot | null>(null);
  const [batchPriceOpen, setBatchPriceOpen] = useState(false);
  const [batchGenerateOpen, setBatchGenerateOpen] = useState(false);
  const [imageRecord, setImageRecord] = useState<ProductSnapshot | null>(null);
  const [filterForm] = Form.useForm<FilterFormValues>();
  const [priceForm] = Form.useForm<PriceFormValues>();
  const [batchPriceForm] = Form.useForm<PriceFormValues>();
  const [batchGenerateForm] = Form.useForm<CoefficientFormValues>();
  const [imageDraft, setImageDraft] = useState<ProductImagesValue>({
    mainImage: '',
    galleryImageUrls: [],
  });

  const tenantsQuery = useQuery({ queryKey: ['operator', 'tenants'], queryFn: listTenants });
  const categoriesQuery = useQuery({
    queryKey: ['operator', 'categories'],
    queryFn: listCategories,
  });
  const brandsQuery = useQuery({
    queryKey: ['operator', 'brands', tenantId],
    queryFn: () => listBrands(tenantId!),
    enabled: tenantId != null,
  });
  const productsQuery = useQuery({
    queryKey: ['operator', 'products', tenantId, page, pageSize, filters],
    queryFn: () =>
      listProducts({
        tenantId: tenantId!,
        page,
        pageSize,
        keyword: filters.keyword?.trim(),
        categoryId: filters.categoryId,
        brandId: filters.brandId,
        status: filters.status,
        priceMinCents: yuanToCents(filters.priceMinYuan),
        priceMaxCents: yuanToCents(filters.priceMaxYuan),
        originalPriceMinCents: yuanToCents(filters.originalPriceMinYuan),
        originalPriceMaxCents: yuanToCents(filters.originalPriceMaxYuan),
      }),
    enabled: tenantId != null,
  });
  const detailQuery = useQuery({
    queryKey: ['operator', 'product-detail', tenantId, detailId],
    queryFn: () => getProduct(tenantId!, detailId!),
    enabled: tenantId != null && detailId != null,
  });

  const tenants = tenantsQuery.data ?? [];
  const tenantMap = useMemo(() => new Map(tenants.map((tenant) => [tenant.id, tenant])), [tenants]);
  const categoryOptions = useMemo(
    () => buildCategoryOptions(categoriesQuery.data ?? []),
    [categoriesQuery.data],
  );
  const categoryMap = useMemo(
    () => new Map((categoriesQuery.data ?? []).map((category) => [category.id, category])),
    [categoriesQuery.data],
  );
  const brandMap = useMemo(
    () => new Map((brandsQuery.data ?? []).map((brand) => [brand.id, brand])),
    [brandsQuery.data],
  );
  const previewImages = parseImages(imageRecord);

  const invalidateProducts = () => {
    void queryClient.invalidateQueries({ queryKey: ['operator', 'products'] });
    if (detailId != null) {
      void queryClient.invalidateQueries({ queryKey: ['operator', 'product-detail'] });
    }
  };

  const priceMutation = useMutation({
    mutationFn: (values: PriceFormValues) =>
      updateProductPrice(tenantId!, priceRecord!.id, yuanToCents(values.priceYuan)!),
    onSuccess: () => {
      const id = priceRecord!.id;
      const newPriceCents = yuanToCents(priceForm.getFieldValue('priceYuan'))!;
      let removedByFilter = false;
      queryClient.setQueriesData<PageResponse<ProductSnapshot>>(
        { queryKey: ['operator', 'products'] },
        (old) => {
          if (!old?.list) return old;
          if (!old.list.some((it) => it.id === id)) return old;
          const current = old.list.find((it) => it.id === id)!;
          const patched = { ...current, priceCents: newPriceCents };
          if (matchesProductFilters(patched, filters)) {
            return { ...old, list: old.list.map((it) => (it.id === id ? patched : it)) };
          }
          removedByFilter = true;
          return { ...old, list: old.list.filter((it) => it.id !== id), total: Math.max(0, old.total - 1) };
        },
      );
      if (detailId === id) {
        queryClient.invalidateQueries({ queryKey: ['operator', 'product-detail'] });
      }
      toast.success('已修改售价');
      if (removedByFilter) {
        toast.info('商品新价格不再符合当前筛选条件，已从列表移除');
      }
      setPriceRecord(null);
      priceForm.resetFields();
    },
    onError: (err: Error) => {
      toast.error(err instanceof ApiError ? err.message : '改售价失败');
    },
  });

  const batchPriceMutation = useMutation({
    mutationFn: (values: PriceFormValues) =>
      batchUpdateProductPrice(tenantId!, selectedProductIds, yuanToCents(values.priceYuan)!),
    onSuccess: (res) => {
      toast.success(`已修改 ${res.updated} 个商品售价`);
      setBatchPriceOpen(false);
      setSelectedProductIds([]);
      batchPriceForm.resetFields();
      invalidateProducts();
    },
    onError: (err: Error) => {
      toast.error(err instanceof ApiError ? err.message : '批量改售价失败');
    },
  });

  const batchGenerateMutation = useMutation({
    mutationFn: (values: CoefficientFormValues) =>
      batchGenerateProductPrice(tenantId!, selectedProductIds, values.coefficient),
    onSuccess: (res) => {
      const selectedCount = selectedProductIds.length;
      const skipped = selectedCount - res.updated;
      toast.success(
        skipped > 0
          ? `已生成 ${res.updated} 个商品售价（${skipped} 个无原价已跳过）`
          : `已生成 ${res.updated} 个商品售价`,
      );
      setBatchGenerateOpen(false);
      setSelectedProductIds([]);
      batchGenerateForm.resetFields();
      invalidateProducts();
    },
    onError: (err: Error) => {
      toast.error(err instanceof ApiError ? err.message : '按系数生成售价失败');
    },
  });

  const unpublishMutation = useMutation({
    mutationFn: (record: ProductSnapshot) => unpublishProduct(tenantId!, record.id),
    onSuccess: () => {
      toast.success('已下架商品');
      invalidateProducts();
    },
    onError: (err: Error) => {
      toast.error(err instanceof ApiError ? err.message : '下架失败');
    },
  });

  const batchUnpublishMutation = useMutation({
    mutationFn: () => batchUnpublishProducts(tenantId!, selectedProductIds),
    onSuccess: (res) => {
      toast.success(`已下架 ${res.updated} 个商品`);
      setSelectedProductIds([]);
      invalidateProducts();
    },
    onError: (err: Error) => {
      toast.error(err instanceof ApiError ? err.message : '批量下架失败');
    },
  });

  const batchPublishMutation = useMutation({
    mutationFn: () => batchPublishProducts(tenantId!, selectedProductIds),
    onSuccess: (res) => {
      toast.success(`已上架 ${res.updated} 个商品`);
      setSelectedProductIds([]);
      invalidateProducts();
    },
    onError: (err: Error) => {
      toast.error(err instanceof ApiError ? err.message : '批量上架失败');
    },
  });

  const batchDeleteMutation = useMutation({
    mutationFn: () => batchDeleteProducts(tenantId!, selectedProductIds),
    onSuccess: (res) => {
      toast.success(`已删除 ${res.updated} 个商品`);
      setSelectedProductIds([]);
      invalidateProducts();
    },
    onError: (err: Error) => {
      toast.error(err instanceof ApiError ? err.message : '批量删除失败');
    },
  });

  const syncLockMutation = useMutation({
    mutationFn: ({ record, locked }: { record: ProductSnapshot; locked: boolean }) =>
      setProductSyncLock(tenantId!, record.id, locked),
    onSuccess: (_, variables) => {
      toast.success(variables.locked ? '已锁定，后续同步将跳过' : '已解锁，后续同步可覆盖');
      invalidateProducts();
    },
    onError: (err: Error) => {
      toast.error(err instanceof ApiError ? err.message : '操作失败');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => batchDeleteProducts(tenantId!, [id]),
    onSuccess: () => {
      toast.success('已删除');
      if (detailId != null) {
        setDetailId(null);
      }
      invalidateProducts();
    },
    onError: (err: Error) => {
      toast.error(err instanceof ApiError ? err.message : '删除失败');
    },
  });

  const selectTenant = (value: number) => {
    setTenantId(value);
    setPage(1);
    setFilters({});
    setSelectedProductIds([]);
    filterForm.resetFields();
  };

  const openPrice = (record: ProductSnapshot) => {
    setPriceRecord(record);
    priceForm.setFieldsValue({ priceYuan: record.priceCents / 100 });
  };

  const columns: TableProps<ProductSnapshot>['columns'] = [
    {
      title: '主图',
      dataIndex: 'mainImage',
      width: 90,
      fixed: 'left',
      render: (_: string | null, record) => {
        const images = parseImages(record);
        if (images.length === 0) {
          return <span style={{ color: '#bfbfbf' }}>—</span>;
        }
        return (
          <Image
            src={images[0]}
            width={52}
            height={52}
            preview={false}
            style={{ objectFit: 'cover', cursor: 'pointer' }}
            onClick={() => setImageRecord(record)}
          />
        );
      },
    },
    {
      title: '商品编码',
      dataIndex: 'productCode',
      width: 150,
      render: (code: string) => <code className="mall-row-code">{code}</code>,
    },
    {
      title: '名称',
      dataIndex: 'name',
      width: 240,
      ellipsis: true,
      render: (name: string, record) => (
        <div>
          <Button
            className="mall-row-title"
            type="link"
            size="small"
            style={{ height: 'auto', padding: 0, textAlign: 'left', whiteSpace: 'normal' }}
            onClick={() => setDetailId(record.id)}
          >
            {name}
          </Button>
          <div className="mall-row-subtitle">{record.nameEn || '未维护英文名'}</div>
        </div>
      ),
    },
    {
      title: '商户',
      dataIndex: 'tenantId',
      width: 150,
      render: (id: number) => tenantMap.get(id)?.name ?? `#${id}`,
    },
    {
      title: '分类 / 品牌',
      key: 'categoryBrand',
      width: 210,
      render: (_, record) => {
        const category = record.categoryId ? categoryMap.get(record.categoryId) : undefined;
        const brand = record.brandId ? brandMap.get(record.brandId) : undefined;
        return (
          <Space direction="vertical" size={0}>
            <span>{category ? categoryDisplayName(category) : '未分类'}</span>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {brand?.name ?? '无品牌'}
            </Typography.Text>
          </Space>
        );
      },
    },
    {
      title: '售价',
      dataIndex: 'priceCents',
      width: 110,
      align: 'right',
      render: (cents: number) => PRICE(cents),
    },
    {
      title: '原价',
      dataIndex: 'originalPriceCents',
      width: 110,
      align: 'right',
      render: (cents: number | null) => (cents == null ? '—' : PRICE(cents)),
    },
    {
      title: '库存',
      dataIndex: 'stock',
      width: 80,
      align: 'right',
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (status: number | null | undefined) => {
        const value = status ?? 0;
        return (
          <Tag color={STATUS_COLOR[value] ?? 'default'}>
            {STATUS_LABEL[value] ?? `未知(${value})`}
          </Tag>
        );
      },
    },
    {
      title: '同步',
      key: 'sync',
      width: 130,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          {record.parentProductCode ? (
            <Tag color="blue" style={{ marginInlineEnd: 0 }}>
              来源同步
            </Tag>
          ) : (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              本地商品
            </Typography.Text>
          )}
          <Button
            size="small"
            type={record.syncLocked ? 'primary' : 'default'}
            icon={record.syncLocked ? <LockOutlined /> : <UnlockOutlined />}
            loading={
              syncLockMutation.isPending && syncLockMutation.variables?.record.id === record.id
            }
            onClick={() =>
              syncLockMutation.mutate({ record, locked: !record.syncLocked })
            }
          >
            {record.syncLocked ? '已锁定' : '锁定'}
          </Button>
        </Space>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 280,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={() => setDetailId(record.id)}>
            查看
          </Button>
          <Button size="small" icon={<DollarOutlined />} onClick={() => openPrice(record)}>
            改售价
          </Button>
          {isPublished(record) ? (
            <Popconfirm
              title={`确认下架「${record.name}」？`}
              okText="下架"
              okButtonProps={{ danger: true }}
              cancelText="取消"
              onConfirm={() => unpublishMutation.mutate(record)}
            >
              <Button
                size="small"
                danger
                icon={<EyeInvisibleOutlined />}
                loading={
                  unpublishMutation.isPending && unpublishMutation.variables?.id === record.id
                }
              >
                下架
              </Button>
            </Popconfirm>
          ) : null}
          <Popconfirm
            title={`确认删除「${record.name}」？`}
            description="删除后商品将从列表中移除，此操作不可撤销。"
            okText="删除"
            okButtonProps={{ danger: true }}
            cancelText="取消"
            onConfirm={() => deleteMutation.mutate(record.id)}
          >
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              loading={deleteMutation.isPending && deleteMutation.variables === record.id}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const detail = detailQuery.data ?? null;

  useEffect(() => {
    if (detail) {
      setImageDraft(snapshotToProductImages(detail));
    }
  }, [detail]);

  const imagesMutation = useMutation({
    mutationFn: () =>
      updateProductImages(tenantId!, detailId!, {
        mainImage: imageDraft.mainImage,
        galleryImageUrls: imageDraft.galleryImageUrls,
      }),
    onSuccess: () => {
      const id = detailId!;
      queryClient.setQueriesData<PageResponse<ProductSnapshot>>(
        { queryKey: ['operator', 'products'] },
        (old) => {
          if (!old?.list) return old;
          if (!old.list.some((it) => it.id === id)) return old;
          return {
            ...old,
            list: old.list.map((it) =>
              it.id === id
                ? { ...it, mainImage: imageDraft.mainImage, images: JSON.stringify(imageDraft.galleryImageUrls) }
                : it,
            ),
          };
        },
      );
      queryClient.invalidateQueries({ queryKey: ['operator', 'product-detail'] });
      toast.success('已保存图片顺序');
    },
    onError: (err: Error) => {
      toast.error(err instanceof ApiError ? err.message : '保存图片失败');
    },
  });

  return (
    <div className="mall-page">
      <div className="mall-page-header">
        <div>
          <div className="mall-page-kicker">Products</div>
          <Typography.Title level={3} className="mall-page-title">
            商品管理
          </Typography.Title>
          <p className="mall-page-description">
            按商户查看商品，调整售价并下架不应继续售卖的商品。
          </p>
        </div>
        {isMobile ? (
          <Button
            icon={<ReloadOutlined />}
            onClick={() => invalidateProducts()}
            disabled={!tenantId}
          >
            刷新
          </Button>
        ) : (
          <Space wrap>
            <Button
              icon={<DollarOutlined />}
              disabled={!tenantId || selectedProductIds.length === 0}
              onClick={() => {
                batchPriceForm.resetFields();
                setBatchPriceOpen(true);
              }}
            >
              批量改售价{selectedProductIds.length > 0 ? ` (${selectedProductIds.length})` : ''}
            </Button>
            <Button
              icon={<CalculatorOutlined />}
              disabled={!tenantId || selectedProductIds.length === 0}
              onClick={() => {
                batchGenerateForm.resetFields();
                setBatchGenerateOpen(true);
              }}
            >
              按系数生成售价{selectedProductIds.length > 0 ? ` (${selectedProductIds.length})` : ''}
            </Button>
            <Popconfirm
              title={`确认上架选中的 ${selectedProductIds.length} 个商品？`}
              okText="上架"
              cancelText="取消"
              disabled={!tenantId || selectedProductIds.length === 0}
              onConfirm={() => batchPublishMutation.mutate()}
            >
              <Button
                icon={<EyeOutlined />}
                disabled={!tenantId || selectedProductIds.length === 0}
                loading={batchPublishMutation.isPending}
              >
                批量上架{selectedProductIds.length > 0 ? ` (${selectedProductIds.length})` : ''}
              </Button>
            </Popconfirm>
            <Popconfirm
              title={`确认下架选中的 ${selectedProductIds.length} 个商品？`}
              okText="下架"
              cancelText="取消"
              disabled={!tenantId || selectedProductIds.length === 0}
              onConfirm={() => batchUnpublishMutation.mutate()}
            >
              <Button
                icon={<EyeInvisibleOutlined />}
                disabled={!tenantId || selectedProductIds.length === 0}
                loading={batchUnpublishMutation.isPending}
              >
                批量下架{selectedProductIds.length > 0 ? ` (${selectedProductIds.length})` : ''}
              </Button>
            </Popconfirm>
            <Popconfirm
              title={`确认删除选中的 ${selectedProductIds.length} 个商品？`}
              description="删除后商品将从列表中移除，此操作不可撤销。"
              okText="删除"
              okButtonProps={{ danger: true }}
              cancelText="取消"
              disabled={!tenantId || selectedProductIds.length === 0}
              onConfirm={() => batchDeleteMutation.mutate()}
            >
              <span>
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  disabled={!tenantId || selectedProductIds.length === 0}
                  loading={batchDeleteMutation.isPending}
                >
                  批量删除{selectedProductIds.length > 0 ? ` (${selectedProductIds.length})` : ''}
                </Button>
              </span>
            </Popconfirm>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => invalidateProducts()}
              disabled={!tenantId}
            >
              刷新
            </Button>
          </Space>
        )}
      </div>
      <div className="mall-filter-card">
        <Form layout="vertical">
          <Row gutter={12}>
            <Col xs={24} md={12} lg={8}>
              <Form.Item label="商户" required>
                <Select
                  showSearch
                  placeholder="请先选择商户"
                  loading={tenantsQuery.isLoading}
                  value={tenantId}
                  onChange={selectTenant}
                  optionFilterProp="label"
                  options={tenants.map((tenant: TenantView) => ({
                    label: `${tenant.name} (${tenant.slug})`,
                    value: tenant.id,
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>
      <FilterDrawer
        activeCount={Object.values(filters).filter((v) => v != null && v !== '').length}
        onSubmit={() => filterForm.submit()}
        onReset={() => {
          filterForm.resetFields();
          setPage(1);
          setFilters({});
          setSelectedProductIds([]);
        }}
      >
        <Form<FilterFormValues>
          form={filterForm}
          layout="vertical"
          disabled={!tenantId}
          onFinish={(values) => {
            setPage(1);
            setFilters(values);
            setSelectedProductIds([]);
          }}
        >
          <Row gutter={12}>
            <Col xs={24} md={8} lg={6}>
              <Form.Item label="关键词" name="keyword">
                <Input
                  allowClear
                  placeholder="名称 / 编码"
                  onPressEnter={() => filterForm.submit()}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8} lg={6}>
              <Form.Item label="分类" name="categoryId">
                <TreeSelect
                  allowClear
                  showSearch
                  treeData={categoryOptions}
                  placeholder="全部分类"
                  loading={categoriesQuery.isLoading}
                  treeNodeFilterProp="title"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8} lg={6}>
              <Form.Item label="品牌" name="brandId">
                <Select
                  allowClear
                  showSearch
                  placeholder="全部品牌"
                  loading={brandsQuery.isLoading}
                  optionFilterProp="label"
                  options={(brandsQuery.data ?? []).map((brand: BrandView) => ({
                    label: brand.name,
                    value: brand.id,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8} lg={6}>
              <Form.Item label="状态" name="status">
                <Select
                  allowClear
                  placeholder="全部状态"
                  options={[
                    { value: 0, label: STATUS_LABEL[0] },
                    { value: 1, label: STATUS_LABEL[1] },
                    { value: 2, label: STATUS_LABEL[2] },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={6}>
              <Form.Item label="售价区间（元）">
                <Space.Compact style={{ width: '100%' }}>
                  <Form.Item name="priceMinYuan" noStyle>
                    <InputNumber
                      min={0}
                      precision={2}
                      placeholder="最低"
                      style={{ width: '50%' }}
                    />
                  </Form.Item>
                  <Form.Item name="priceMaxYuan" noStyle>
                    <InputNumber
                      min={0}
                      precision={2}
                      placeholder="最高"
                      style={{ width: '50%' }}
                    />
                  </Form.Item>
                </Space.Compact>
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={6}>
              <Form.Item label="原价区间（元）">
                <Space.Compact style={{ width: '100%' }}>
                  <Form.Item name="originalPriceMinYuan" noStyle>
                    <InputNumber
                      min={0}
                      precision={2}
                      placeholder="最低"
                      style={{ width: '50%' }}
                    />
                  </Form.Item>
                  <Form.Item name="originalPriceMaxYuan" noStyle>
                    <InputNumber
                      min={0}
                      precision={2}
                      placeholder="最高"
                      style={{ width: '50%' }}
                    />
                  </Form.Item>
                </Space.Compact>
              </Form.Item>
            </Col>
            {!isMobile && (
              <Col xs={24} md={12} lg={6} style={{ display: 'flex', alignItems: 'end' }}>
                <Form.Item>
                  <Space>
                    <Button type="primary" htmlType="submit">
                      查询
                    </Button>
                    <Button
                      onClick={() => {
                        filterForm.resetFields();
                        setPage(1);
                        setFilters({});
                        setSelectedProductIds([]);
                      }}
                    >
                      重置
                    </Button>
                  </Space>
                </Form.Item>
              </Col>
            )}
          </Row>
        </Form>
      </FilterDrawer>
      {!tenantId ? (
        <Typography.Paragraph type="secondary">请先选择商户后查看商品。</Typography.Paragraph>
      ) : null}
      <div className="mall-table-card">
        <div className="mall-table-toolbar">
          <div>
            <div className="mall-table-title">商品列表</div>
            <div className="mall-table-subtitle">
              {tenantId
                ? `共 ${productsQuery.data?.total ?? 0} 个商品${
                    selectedProductIds.length > 0 ? `，已选择 ${selectedProductIds.length} 个` : ''
                  }`
                : '选择商户后展示商品'}
            </div>
          </div>
          <div className="mall-table-actions">
            <Button
              size="small"
              disabled={selectedProductIds.length === 0}
              onClick={() => setSelectedProductIds([])}
            >
              清空选择
            </Button>
          </div>
        </div>
        <ResponsiveList<ProductSnapshot>
          rowKey={(record) => record.id}
          loading={productsQuery.isLoading}
          dataSource={productsQuery.data?.list ?? []}
          selectedRowKeys={selectedProductIds}
          onSelectChange={(keys) => setSelectedProductIds(keys.map((key) => Number(key)))}
          emptyText={tenantId ? '暂无商品' : '请先选择商户'}
          pagination={{
            current: page,
            pageSize,
            total: productsQuery.data?.total ?? 0,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100, 200, 500, 1000, 2000, 5000],
            showTotal: (total, range) => `${range[0]}-${range[1]} / 共 ${total} 条`,
            onChange: (nextPage, nextPageSize) => {
              setPage(nextPage);
              setPageSize(nextPageSize);
            },
          }}
          tableProps={{
            size: 'small',
            columns,
            scroll: { x: 1510 },
            locale: { emptyText: tenantId ? '暂无商品' : '请先选择商户' },
            rowSelection: {
              selectedRowKeys: selectedProductIds,
              onChange: (keys) => setSelectedProductIds(keys.map((key) => Number(key))),
            },
          }}
          renderCard={(record) => {
            const images = parseImages(record);
            const category = record.categoryId ? categoryMap.get(record.categoryId) : undefined;
            const brand = record.brandId ? brandMap.get(record.brandId) : undefined;
            const status = record.status ?? 0;
            return (
              <div>
                <div style={{ display: 'flex', gap: 12 }}>
                  {images.length > 0 ? (
                    <Image
                      src={images[0]}
                      width={56}
                      height={56}
                      preview={false}
                      style={{ objectFit: 'cover', borderRadius: 8, cursor: 'pointer', flex: 'none' }}
                      onClick={() => setImageRecord(record)}
                    />
                  ) : (
                    <div
                      style={{
                        width: 56,
                        height: 56,
                        borderRadius: 8,
                        background: '#f1f5f9',
                        color: '#bfbfbf',
                        display: 'grid',
                        placeItems: 'center',
                        flex: 'none',
                      }}
                    >
                      —
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Button
                      className="mall-row-title"
                      type="link"
                      size="small"
                      style={{ height: 'auto', padding: 0, textAlign: 'left', whiteSpace: 'normal' }}
                      onClick={() => setDetailId(record.id)}
                    >
                      {record.name}
                    </Button>
                    <div className="mall-row-subtitle">{record.productCode}</div>
                    <div
                      style={{
                        marginTop: 4,
                        display: 'flex',
                        gap: 6,
                        flexWrap: 'wrap',
                        alignItems: 'center',
                      }}
                    >
                      <Tag color={STATUS_COLOR[status] ?? 'default'}>
                        {STATUS_LABEL[status] ?? `未知(${status})`}
                      </Tag>
                      <span style={{ fontSize: 12, color: '#64748b' }}>
                        {category ? categoryDisplayName(category) : '未分类'} · {brand?.name ?? '无品牌'}
                      </span>
                    </div>
                    <div style={{ marginTop: 4 }}>
                      <span style={{ fontWeight: 600 }}>{PRICE(record.priceCents)}</span>
                      <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 8 }}>
                        原价 {record.originalPriceCents == null ? '—' : PRICE(record.originalPriceCents)}
                      </span>
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                  <Button size="small" icon={<EyeOutlined />} onClick={() => setDetailId(record.id)}>
                    查看
                  </Button>
                  <Button size="small" icon={<DollarOutlined />} onClick={() => openPrice(record)}>
                    改售价
                  </Button>
                  <Button
                    size="small"
                    type={record.syncLocked ? 'primary' : 'default'}
                    icon={record.syncLocked ? <LockOutlined /> : <UnlockOutlined />}
                    onClick={() => syncLockMutation.mutate({ record, locked: !record.syncLocked })}
                  >
                    {record.syncLocked ? '已锁定' : '锁定'}
                  </Button>
                  {isPublished(record) ? (
                    <Popconfirm
                      title={`确认下架「${record.name}」？`}
                      okText="下架"
                      okButtonProps={{ danger: true }}
                      cancelText="取消"
                      onConfirm={() => unpublishMutation.mutate(record)}
                    >
                      <Button size="small" danger icon={<EyeInvisibleOutlined />}>
                        下架
                      </Button>
                    </Popconfirm>
                  ) : null}
                  <Popconfirm
                    title={`确认删除「${record.name}」？`}
                    okText="删除"
                    okButtonProps={{ danger: true }}
                    cancelText="取消"
                    onConfirm={() => deleteMutation.mutate(record.id)}
                  >
                    <Button size="small" danger icon={<DeleteOutlined />}>
                      删除
                    </Button>
                  </Popconfirm>
                </div>
              </div>
            );
          }}
        />
      </div>
      {isMobile && tenantId && selectedProductIds.length > 0 && (
        <div className="mall-batch-bar">
          <span className="mall-batch-bar-count">已选 {selectedProductIds.length} 项</span>
          <Space size={8} wrap>
            <Button
              size="small"
              icon={<DollarOutlined />}
              onClick={() => {
                batchPriceForm.resetFields();
                setBatchPriceOpen(true);
              }}
            >
              改售价
            </Button>
            <Button
              size="small"
              icon={<CalculatorOutlined />}
              onClick={() => {
                batchGenerateForm.resetFields();
                setBatchGenerateOpen(true);
              }}
            >
              按系数
            </Button>
            <Popconfirm
              title={`确认上架选中的 ${selectedProductIds.length} 个商品？`}
              okText="上架"
              cancelText="取消"
              onConfirm={() => batchPublishMutation.mutate()}
            >
              <Button size="small" icon={<EyeOutlined />} loading={batchPublishMutation.isPending}>
                上架
              </Button>
            </Popconfirm>
            <Popconfirm
              title={`确认下架选中的 ${selectedProductIds.length} 个商品？`}
              okText="下架"
              cancelText="取消"
              onConfirm={() => batchUnpublishMutation.mutate()}
            >
              <Button size="small" icon={<EyeInvisibleOutlined />} loading={batchUnpublishMutation.isPending}>
                下架
              </Button>
            </Popconfirm>
            <Popconfirm
              title={`确认删除选中的 ${selectedProductIds.length} 个商品？`}
              okText="删除"
              okButtonProps={{ danger: true }}
              cancelText="取消"
              onConfirm={() => batchDeleteMutation.mutate()}
            >
              <Button size="small" danger icon={<DeleteOutlined />} loading={batchDeleteMutation.isPending}>
                删除
              </Button>
            </Popconfirm>
            <Button size="small" type="text" onClick={() => setSelectedProductIds([])}>
              取消
            </Button>
          </Space>
        </div>
      )}
      <Drawer
        title="商品详情"
        open={detailId != null}
        width={isMobile ? '100%' : 760}
        onClose={() => setDetailId(null)}
        destroyOnClose
      >
        {detailQuery.isLoading ? (
          <Typography.Paragraph>加载中...</Typography.Paragraph>
        ) : detail ? (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Descriptions bordered column={isMobile ? 1 : 2} size="small">
              <Descriptions.Item label="商品编码">{detail.productCode}</Descriptions.Item>
              <Descriptions.Item label="状态">
                <Tag color={STATUS_COLOR[detail.status ?? 0] ?? 'default'}>
                  {STATUS_LABEL[detail.status ?? 0] ?? '未知'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="名称" span={2}>
                {detail.name}
              </Descriptions.Item>
              <Descriptions.Item label="英文名称" span={2}>
                {detail.nameEn || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="售价">{PRICE(detail.priceCents)}</Descriptions.Item>
              <Descriptions.Item label="原价">
                {detail.originalPriceCents == null ? '—' : PRICE(detail.originalPriceCents)}
              </Descriptions.Item>
              <Descriptions.Item label="库存">{detail.stock}</Descriptions.Item>
              <Descriptions.Item label="分类">
                {detail.categoryId && categoryMap.get(detail.categoryId)
                  ? categoryDisplayName(categoryMap.get(detail.categoryId)!)
                  : '未分类'}
              </Descriptions.Item>
              <Descriptions.Item label="品牌">
                {detail.brandId && brandMap.get(detail.brandId)
                  ? brandMap.get(detail.brandId)!.name
                  : '无品牌'}
              </Descriptions.Item>
            </Descriptions>
            <div>
              <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }}>
                <Typography.Text strong>图片</Typography.Text>
                <Button
                  type="primary"
                  size="small"
                  loading={imagesMutation.isPending}
                  disabled={!imageDraft.mainImage.trim()}
                  onClick={() => imagesMutation.mutate()}
                >
                  保存图片
                </Button>
              </Space>
              <ProductImageEditor
                value={imageDraft}
                onChange={setImageDraft}
                onError={(message) => toast.error(message)}
              />
            </div>
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="中文描述">{detail.description || '—'}</Descriptions.Item>
              <Descriptions.Item label="英文描述">{detail.descriptionEn || '—'}</Descriptions.Item>
              <Descriptions.Item label="属性">
                {detail.attributes.length === 0 ? (
                  '—'
                ) : (
                  <Space size={[6, 6]} wrap>
                    {detail.attributes.map((attr) => (
                      <Tag key={attr.id ?? attr.attrKey}>
                        {attr.attrKey}:{' '}
                        {(attr.options ?? []).map((option) => option.zh).join('、') || '—'}
                      </Tag>
                    ))}
                  </Space>
                )}
              </Descriptions.Item>
            </Descriptions>
          </Space>
        ) : (
          <Typography.Paragraph type="danger">商品详情加载失败</Typography.Paragraph>
        )}
      </Drawer>
      <Modal
        title={imageRecord ? `${imageRecord.name} 的全部图片` : '商品图片'}
        open={imageRecord != null}
        footer={null}
        width={isMobile ? '94vw' : 820}
        onCancel={() => setImageRecord(null)}
        destroyOnClose
      >
        <Image.PreviewGroup>
          <Space size={[8, 8]} wrap>
            {previewImages.map((src) => (
              <Image key={src} src={src} width={120} height={120} style={{ objectFit: 'cover' }} />
            ))}
          </Space>
        </Image.PreviewGroup>
      </Modal>
      <Modal
        title={`批量修改售价（${selectedProductIds.length} 个商品）`}
        open={batchPriceOpen}
        onCancel={() => setBatchPriceOpen(false)}
        onOk={() => batchPriceForm.submit()}
        confirmLoading={batchPriceMutation.isPending}
        destroyOnClose
      >
        <Form<PriceFormValues>
          form={batchPriceForm}
          layout="vertical"
          onFinish={(values) => batchPriceMutation.mutate(values)}
        >
          <Form.Item
            label="售价（元）"
            name="priceYuan"
            rules={[{ required: true, message: '请输入售价' }]}
          >
            <InputNumber min={0} precision={2} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title={`按系数生成售价（${selectedProductIds.length} 个商品）`}
        open={batchGenerateOpen}
        onCancel={() => setBatchGenerateOpen(false)}
        onOk={() => batchGenerateForm.submit()}
        confirmLoading={batchGenerateMutation.isPending}
        destroyOnClose
      >
        <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
          售价 = 原价 × 系数（四舍五入到分）；无原价的商品将被跳过。
        </Typography.Paragraph>
        <Form<CoefficientFormValues>
          form={batchGenerateForm}
          layout="vertical"
          onFinish={(values) => batchGenerateMutation.mutate(values)}
        >
          <Form.Item
            label="系数"
            name="coefficient"
            rules={[
              { required: true, message: '请输入系数' },
              {
                validator: (_, value) =>
                  value == null || (value > 0 && value <= 1000)
                    ? Promise.resolve()
                    : Promise.reject(new Error('系数需大于 0 且不超过 1000')),
              },
            ]}
          >
            <InputNumber
              min={0}
              max={1000}
              step={0.01}
              precision={4}
              placeholder="例如 0.85"
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title={priceRecord ? `修改售价：${priceRecord.name}` : '修改售价'}
        open={priceRecord != null}
        onCancel={() => setPriceRecord(null)}
        onOk={() => priceForm.submit()}
        confirmLoading={priceMutation.isPending}
        destroyOnClose
      >
        <Form<PriceFormValues>
          form={priceForm}
          layout="vertical"
          onFinish={(values) => priceMutation.mutate(values)}
        >
          <Form.Item
            label="售价（元）"
            name="priceYuan"
            rules={[{ required: true, message: '请输入售价' }]}
          >
            <InputNumber min={0} precision={2} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
